import React, { useEffect, useMemo, useState } from 'react';
import { ethers } from 'ethers';
import Button from '../common/Button';
import { useWallet } from '../../context/WalletContext';
import { useContractState } from '../../context/ContractContext';
import { useNotify } from '../../context/NotificationContext';
import { getADNFTContract, getUSDTContract, getADNFTAddress } from '../../web3/contractInstance';
import { validateInvestment } from '../../utils/validators';
import { parseContractError } from '../../utils/errors';
import { formatUSDT, toNumberSafe } from '../../utils/format';
import { toUnits } from '../../web3/providerHelper';
import FullScreenLoader from '../loader/FullScreenLoader';

const QUICK = [50, 100, 500, 1000];

// Reads referrer from URL ?ref=NNN on mount so referral links "just work".
function refFromUrl() {
  try {
    const u = new URL(window.location.href);
    const r = u.searchParams.get('ref');
    if (r && /^\d+$/.test(r)) return r;
  } catch (_e) {}
  return '';
}

export default function RegisterAndInvestForm() {
  const { getSigner } = useWallet();
  const { limits, usdtBalance, refresh } = useContractState();
  const notify = useNotify();
  const [amount, setAmount] = useState('');
  const [refId, setRefId] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState('');

  useEffect(() => { setRefId(refFromUrl()); }, []);

  const err = useMemo(() => amount ? validateInvestment(amount, limits) : null, [amount, limits]);
  const balNum = toNumberSafe(usdtBalance);
  const min = limits ? toNumberSafe(limits.min) : 50;
  const mult = limits ? toNumberSafe(limits.multiple) : 50;

  async function ensureApproval(signer, needed) {
    const usdt = getUSDTContract(signer);
    const owner = await signer.getAddress();
    const cur = await usdt.allowance(owner, getADNFTAddress());
    if (cur.gte(needed)) return;
    setBusyMsg('Approving USDT…');
    const tx = await usdt.approve(getADNFTAddress(), ethers.constants.MaxUint256);
    await tx.wait();
    notify.success('USDT approved');
  }

  async function submit() {
    if (err) { notify.error('Invalid amount', err); return; }
    const signer = getSigner();
    if (!signer) { notify.error('Wallet not ready'); return; }
    const value = toUnits(amount);
    if (Number(amount) > balNum) { notify.error('Insufficient USDT balance'); return; }

    setBusy(true);
    setBusyMsg('Preparing transaction…');
    try {
      await ensureApproval(signer, value);
      const c = getADNFTContract(signer);
      setBusyMsg('Confirm registration in your wallet…');
      const rid = ethers.BigNumber.from(refId && /^\d+$/.test(refId) ? refId : '0');
      const tx = await c.registerAndInvest(rid, value);
      setBusyMsg('Waiting for confirmation…');
      await tx.wait();
      notify.success('Welcome to Adrenaline!', `$${amount} USDT staked`);
      setAmount('');
      refresh();
    } catch (e) {
      notify.error('Registration failed', parseContractError(e));
    } finally {
      setBusy(false);
      setBusyMsg('');
    }
  }

  return (
    <div className="card invest-card">
      <div className="row-between mb-8">
        <h3>Register & Invest</h3>
        <span className="badge success">Bal: {formatUSDT(usdtBalance)}</span>
      </div>
      <div className="field">
        <label>Referrer ID (optional)</label>
        <input
          className="input"
          type="text"
          inputMode="numeric"
          placeholder="e.g. 5000"
          value={refId}
          onChange={e => setRefId(e.target.value.replace(/\D/g, ''))}
        />
        <div className="hint-msg">Leave empty to use the default referrer.</div>
      </div>
      <div className="field">
        <label>Amount (USDT)</label>
        <div className="input-suffix">
          <input
            className="input"
            type="number"
            inputMode="decimal"
            placeholder={`min $${min}, step $${mult}`}
            value={amount}
            onChange={e => setAmount(e.target.value)}
          />
          <span className="suffix">USDT</span>
        </div>
        {err && <div className="error-msg">⚠ {err}</div>}
      </div>
      <div className="invest-quick">
        {QUICK.map(q => (
          <button key={q} className={`chip ${String(q) === amount ? 'active' : ''}`} onClick={() => setAmount(String(q))}>${q}</button>
        ))}
      </div>
      <Button neon block size="lg" onClick={submit} loading={busy} disabled={!amount || !!err}>
        Register & Invest
      </Button>
      {busy && <FullScreenLoader message={busyMsg || 'Please wait…'} sub="Do not close this window" />}
    </div>
  );
}
